import { lazy } from 'react'
import { LAYOUT_BLANK } from '@/constants/theme.constant'
import type { Routes } from '@/@types/routes'

/**
 * Routes that work signed in **and** signed out.
 *
 * A third group, because the two existing ones both redirect: `ProtectedRoute`
 * sends a stranger to sign-in, and `PublicRoute` sends a signed-in user to the
 * dashboard. Either would break an invitation link — the first loses the
 * preview that names the organisation, which is the phishing mitigation, and
 * the second bounces the colleague who is already signed in and came to accept.
 *
 * Keep this list very short. A route here has no guard at all, so its component
 * owns every access decision it makes.
 */
const openRoute: Routes = [
    {
        // Accepting an invitation. The recipient may not have an account yet,
        // and the page's job before sign-in is to name the organisation that
        // invited them. Accepting itself needs them signed in as the invited
        // address, which the API enforces and the page explains — rather than
        // redirecting, which would lose the token from the URL.
        key: 'acceptInvite',
        path: `/invite/:token`,
        component: lazy(() => import('@/views/invite')),
        authority: [],
    },
    // The public booking pages (2026-10-15). Read by strangers, so they are
    // open; a signed-in host previewing their own page must see it as a
    // stranger would, hence the blank layout — no side nav, no usage banner
    // (Layouts.tsx skips the billing chrome on these paths too). Every access
    // decision is the API's: an unknown handle, a secret link, a switched-off
    // one all answer 404 alike.
    {
        key: 'bookHost',
        path: '/book/:handle',
        component: lazy(() => import('@/views/booking/public/HostPage')),
        authority: [],
        meta: { layout: LAYOUT_BLANK },
    },
    {
        key: 'bookLink',
        path: '/book/:handle/:slug',
        component: lazy(() => import('@/views/booking/public/BookLinkPage')),
        authority: [],
        meta: { layout: LAYOUT_BLANK },
    },
    {
        key: 'bookConfirmed',
        path: '/book/:handle/:slug/confirmed/:id',
        component: lazy(() => import('@/views/booking/public/ConfirmedPage')),
        authority: [],
        meta: { layout: LAYOUT_BLANK },
    },
    {
        // The invitee's own booking, reached from their confirmation mail.
        // The token in the path is the whole credential: cancel and
        // reschedule only, for one booking.
        key: 'bookManage',
        path: '/bookings/manage/:token',
        component: lazy(() => import('@/views/booking/public/ManagePage')),
        authority: [],
        meta: { layout: LAYOUT_BLANK },
    },
]

export default openRoute
